// Chart-axis tick helpers. Pure functions extracted so the
// width-aware decimation math can be unit-tested in the node-only
// vitest env without dragging in a DOM or visx.
//
// Background: the TaperChart's Full view used to push one tick per
// week unconditionally. A 12-week plan rendered 12 `wk N` labels into
// ~250 px of inner chart width on a 360 px viewport, and the labels
// overlapped into an illegible smear. This helper thins the boundary
// list so the rendered labels can breathe at any width.

export interface PickWeekTickValuesArgs {
  /** Inclusive day-index window start. */
  startIndex: number;
  /** Inclusive day-index window end. */
  endIndex: number;
  /** Chart's inner (post-margin) width in CSS px. */
  innerWidth: number;
  /**
   * Estimated rendered label width including breathing room, in CSS px.
   * Defaults to 44 — wide enough that "wk 11" (≈ 28 px at fontSize 11)
   * never butts against its neighbor. Bump it if a label format changes.
   */
  labelWidthPx?: number;
}

/**
 * Pick a set of day-index tick positions for the TaperChart x-axis when
 * rendering as `wk N` labels.
 *
 * Returns a subset of week-boundary day-indices inside `[startIndex,
 * endIndex]`, decimated so that at most `floor(innerWidth /
 * labelWidthPx)` ticks are emitted. The first emitted tick is always
 * the first week boundary inside the window; subsequent ticks are
 * spaced by a uniform `stride` (in weeks). The rightmost emitted tick
 * may sit a few weeks short of `endIndex` — the chart's right edge is
 * visually anchored by the last data point, not a label.
 *
 * Edge cases:
 *  - Returns `[]` for empty/inverted windows or non-positive widths.
 *  - When the window straddles no week boundary (rare: a 3-day
 *    non-aligned window), returns the window midpoint so an axis still
 *    draws.
 */
export function pickWeekTickValues(args: PickWeekTickValuesArgs): number[] {
  const { startIndex, endIndex, innerWidth } = args;
  const labelWidth = args.labelWidthPx ?? 44;

  if (endIndex < startIndex || innerWidth <= 0 || labelWidth <= 0) return [];

  const maxTicks = Math.max(2, Math.floor(innerWidth / labelWidth));

  const firstWeek = Math.ceil(startIndex / 7) * 7;
  const boundaries: number[] = [];
  for (let i = firstWeek; i <= endIndex; i += 7) boundaries.push(i);

  if (boundaries.length === 0) {
    return [Math.round((startIndex + endIndex) / 2)];
  }
  if (boundaries.length <= maxTicks) return boundaries;

  const stride = Math.ceil(boundaries.length / maxTicks);
  const out: number[] = [];
  for (let i = 0; i < boundaries.length; i += stride) out.push(boundaries[i]);
  return out;
}
